import './ContextGap.scss';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { Column } from 'primereact/column';
import { DataTable, DataTableExpandedRows, DataTableRowToggleEvent } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { SuggestedTopic } from '../../../../shared/models/projects/ProjectResponse';
import { KeywordsData } from '../../../../shared/models/tags/TagsResponse';
import { TopicsDistribution } from '../../../../shared/models/context-insights/ContextInsightsResponse';
import { keywordsTemplate } from '../../../../shared/helpers/ColumnHelper';

interface ContextGapProps {
  contextGapTopics: TopicsDistribution[];
  suggestedTopics: SuggestedTopic[];
  commonKeywords: KeywordsData[];
  isLoading: boolean;
}

const ContextGap: React.FC<ContextGapProps> = ({
  contextGapTopics,
  suggestedTopics,
  commonKeywords,
  isLoading,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'pl';

  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});

  const handleRowToggle = useCallback((e: DataTableRowToggleEvent) => {
    setExpandedRows(e.data as DataTableExpandedRows);
  }, []);

  const rowExpansionTemplate = useCallback(
    (data: TopicsDistribution) => {
      const matchedTopic = suggestedTopics.find(
        (st) => st.name.toLowerCase().trim() === data.topic.toLowerCase().trim()
      );

      const description =
        currentLang === 'pl'
          ? matchedTopic?.gap_description_pl
          : matchedTopic?.gap_description_en;

      if (!description) {
        return (
          <div className="context-gap__expansion-empty">
            {t('contextAnalysis.gapDescriptionGenerating')}
          </div>
        );
      }

      return (
        <div className="context-gap__gap-description">
          {description}
        </div>
      );
    },
    [suggestedTopics, currentLang, t]
  );

  const emptyTemplate = () => {
    return (
      <div className="no-results">
        <EmptyPanel />
      </div>
    );
  };

  const hasGapDescriptions = suggestedTopics.some(
    (st) => st.gap_description_en || st.gap_description_pl
  );

  return (
    <div className="stats__panel content-gap">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('contextAnalysis.contextGap')}
          <CustomTooltip tooltipText="contextAnalysis.contextGapTooltip" addMargin={false} />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="skeleton--table skeleton--table-4">
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          stripedRows
          paginator={true}
          scrollable
          paginatorClassName="pagination"
          removableSort
          dataKey="topic"
          value={contextGapTopics}
          emptyMessage={emptyTemplate}
          rows={10}
          expandedRows={expandedRows}
          onRowToggle={handleRowToggle}
          rowExpansionTemplate={rowExpansionTemplate}
          rowsPerPageOptions={[10, 25, 50]}
          tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
          tableClassName="table"
          className="table-wrapper table-wrapper--accordion"
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <Column
            header={t('topic')}
            field="topic"
            headerClassName="table__header"
            body={(data: TopicsDistribution) => (
              <div
                style={{ display: 'flex', alignItems: 'center', fontWeight: '600' }}
                className="topic-name"
              >
                {suggestedTopics.find(
                  (st) => st.name.toLowerCase().trim() === data.topic.toLowerCase().trim()
                )?.[currentLang === 'pl' ? 'namePl' : 'name'] ?? data.topic}
              </div>
            )}
            style={{ width: '220px' }}
          />
          <Column
            header={t('topBrands')}
            headerClassName="table__header"
            body={(data: TopicsDistribution) => (
              <div className="tags">
                {keywordsTemplate(
                  data.competitorBrands.map(
                    (kw: string) =>
                      commonKeywords.find((k) => k.normalizedKeyword === kw)?.keyword || kw
                  ),
                  5,
                  'tag tag--brand',
                  ''
                )}
              </div>
            )}
            style={{ minWidth: '150px' }}
          />
          <Column
            header={t('occurrences')}
            field="count"
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            sortable
            style={{ width: '140px' }}
          />
          <Column expander={true} style={{ width: '25px' }} hidden={!hasGapDescriptions} />
        </DataTable>
      )}
    </div>
  );
};

export default ContextGap;